import React from 'react';
import { useCurrentuser, useSetTimezone, useTimezone } from '@uplift/contexts';
import { SimpleMenu } from '../lib/simple-menu/simple-menu.component';
import { useUpdateUserTimezone } from './UserTimezoneSelect.hooks';
import { Timezone, UserRole } from '@uplift/types';
import { useAllTimezonesQuery } from '@uplift/queries';
import { Button } from '../Button';
import { ArrowDropDown, Public } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { ClassNameMap } from '@mui/material';

export type UserTimezoneSelectProps = {
  button: (options: {
    onClick: React.MouseEventHandler<HTMLElement>;
    label: string;
  }) => React.ReactElement;
  labelKey?: keyof Timezone;
  formatLabel?: (tz: Timezone) => string;
  userRole?: UserRole;
};

export const UserTimezoneSelect: React.VFC<UserTimezoneSelectProps> = ({
  button,
  formatLabel,
  userRole,
  labelKey = 'fullName'
}) => {
  const me = useCurrentuser();
  const timezone = useTimezone();
  const setTimezone = useSetTimezone();
  const updateUserTimezone = useUpdateUserTimezone();
  const { data: timezones = [] } = useAllTimezonesQuery(me?.role ?? userRole);

  const label = React.useMemo((): string => {
    const tzItem = timezones.find(tz => tz.ianaName === timezone);

    if (formatLabel && tzItem) {
      const formatted = formatLabel(tzItem);

      if (formatted) {
        return formatted;
      }
    }

    return tzItem?.[labelKey]?.toString() ?? timezone;
  }, [ timezone, timezones, labelKey, formatLabel ]);

  const onChange = React.useCallback(async tz => {
    setTimezone?.(tz.ianaName);

    if (!me?.id) {
      return;
    }

    await updateUserTimezone({ userId: me.id, timezone: tz.ianaName });
  }, [ me, setTimezone, updateUserTimezone ]);

  return (
    <SimpleMenu
      button={(opts) => button({ ...opts, label })}
      valueKey="ianaName"
      labelKey="fullName"
      options={timezones}
      onChange={onChange}
    />
  );
};


export type StandardTimezoneSelectProps = Partial<UserTimezoneSelectProps> & {
  disabled?: boolean;
  classes?: Partial<ClassNameMap<'button' | 'buttonIcon'>>;
};

export const StandardTimezoneSelect: React.VFC<StandardTimezoneSelectProps> = ({ disabled, classes: propClasses, ...props }) => {
  const { classes } = useStandardTimezoneSelectStyles(undefined, {
    props: {
      classes: propClasses
    }
  });

  return (
    <UserTimezoneSelect
      labelKey="fullName"
      formatLabel={(tz) => tz?.fullName?.split(' — ')?.[1] ?? tz?.fullName}
      button={opts => (
        <Button
          disabled={disabled}
          onClick={opts.onClick}
          className={classes.button}
          size="small"
          startIcon={<Public className={classes.buttonIcon} />}
          endIcon={<ArrowDropDown />}
        >
          {opts.label}
        </Button>
      )}
      {...props}
    />
  );
};

const useStandardTimezoneSelectStyles = makeStyles<void, 'buttonIcon'>({ name: 'StandardTimezoneSelect' })((theme, _params, classes) => ({
  button: {
    textTransform: 'none',
    fontSize: '13px',
    '&:hover': {
      background: 'transparent',
    },
    '&:disabled': {
      [`& .${classes.buttonIcon}`]: {
        color: 'currentColor',
      },
    }
  },
  buttonIcon: {
    color: theme.palette.text.secondary,
  }
}));
